import React, { useState, useEffect } from 'react';
import { Box, Image, Button, VStack, Icon, Text, useToast, IconButton } from '@chakra-ui/react';
import { FiFileText, FiExternalLink, FiChevronLeft, FiChevronRight } from 'react-icons/fi';

const TeamActivityHeroImage = ({ image, title, isVideo, videoUrl, images = [] }) => {
  const toast = useToast();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Create array of all available images
  const allImages = [];
  
  // Add the main image (legacy support)
  if (image) {
    allImages.push({
      url: image,
      alternativeText: title || 'Blog image',
      name: 'Main image'
    });
  }
  
  // Add images from the new images array
  if (images && Array.isArray(images)) {
    images.forEach(img => {
      // Handle both new object format and legacy string format
      if (img && typeof img === 'object' && img.url) {
        // New format: object with url, alternativeText, etc.
        const imageUrl = img.url;
        if (!allImages.some(existing => existing.url === imageUrl)) {
          allImages.push(img);
        }
      } else if (img && typeof img === 'string') {
        // Legacy format: direct URL string
        if (!allImages.some(existing => existing.url === img)) {
          allImages.push({
            url: img,
            alternativeText: title || 'Blog image',
            name: 'Image'
          });
        }
      }
    });
  }

  // Check if we have multiple images for carousel
  const hasMultipleImages = allImages.length > 1;

  // Auto-slideshow effect (6 seconds per image)
  useEffect(() => {
    if (hasMultipleImages) {
      const interval = setInterval(() => {
        setCurrentImageIndex(prev => (prev === allImages.length - 1 ? 0 : prev + 1));
      }, 6000); // 6 seconds

      return () => clearInterval(interval);
    }
  }, [hasMultipleImages, allImages.length]);

  // Navigation functions
  const goToPrevious = () => {
    setCurrentImageIndex(prev => (prev === 0 ? allImages.length - 1 : prev - 1));
  };

  const goToNext = () => {
    setCurrentImageIndex(prev => (prev === allImages.length - 1 ? 0 : prev + 1));
  };

  const currentImageObj = allImages[currentImageIndex];
  const currentImage = currentImageObj?.url || image;

  // Helper function to check if URL is a PDF
  const isPdfFile = (url) => {
    if (!url) return false;
    const urlLower = url.toLowerCase();
    return urlLower.endsWith('.pdf') || urlLower.includes('.pdf');
  };

  // Helper function to open PDF in new window
  const openPdfInNewWindow = (pdfUrl) => {
    try {
      window.open(pdfUrl, '_blank', 'noopener,noreferrer');
    } catch (error) {
      console.error('Failed to open PDF:', error);
      toast({
        title: 'Error',
        description: 'Failed to open PDF file. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Check media types
  const hasValidVideo = isVideo && videoUrl;
  const isPdf = isPdfFile(currentImage);
  const hasValidImage = currentImage && 
    !currentImage.includes('/clients/') && 
    !currentImage.includes('/png/placeholder.png') &&
    !currentImage.includes('placeholder') &&
    !isPdf;

  // Only render if we have valid media
  if (!hasValidVideo && !hasValidImage && !isPdf) {
    return null;
  }

  return (
    <Box mb={8}>
      {hasValidVideo ? (
        <video
          src={videoUrl}
          alt={title}
          style={{
            borderRadius: '5px',
            width: '100%',
            height: 'auto',
            maxHeight: '600px',
            objectFit: 'cover'
          }}
          controls
          autoPlay
          muted
          loop
        />
      ) : isPdf ? (
        <Box
          position="relative"
          borderRadius="5px"
          border="2px dashed #e2e8f0"
          bg="gray.50"
          p={8}
          textAlign="center"
          cursor="pointer"
          transition="all 0.2s ease"
          _hover={{
            bg: "gray.100",
            borderColor: "#000B8C",
            transform: "translateY(-2px)",
            boxShadow: "lg",
          }}
          onClick={() => openPdfInNewWindow(currentImage)}
        >
          <VStack spacing={4}>
            <Icon as={FiFileText} boxSize={12} color="#000B8C" />
            <Text fontSize="lg" fontWeight="semibold" color="gray.700">
              PDF Document
            </Text>
            <Text fontSize="sm" color="gray.500" mb={2}>
              Click to open PDF in new window
            </Text>
            <Button
              leftIcon={<Icon as={FiExternalLink} />}
              colorScheme="blue"
              variant="solid"
              size="md"
              bg="#000B8C"
              _hover={{ bg: "#000A7A" }}
              onClick={(e) => {
                e.stopPropagation();
                openPdfInNewWindow(currentImage);
              }}
            >
              Open PDF
            </Button>
          </VStack>
        </Box>
      ) : (
        <Box position="relative">
          {/* Main Image */}
          <Image
            src={currentImage}
            alt={currentImageObj?.alternativeText || title || 'Blog image'}
            borderRadius="5px"
            w="100%"
            h={{ base: '200px', md: '600px' }}
            objectFit="cover"
          />
          
          {/* Carousel Navigation - Only show if multiple images */}
          {hasMultipleImages && (
            <>
              {/* Previous Button */}
              <IconButton
                icon={<Icon as={FiChevronLeft} />}
                position="absolute"
                left="10px"
                top="50%"
                transform="translateY(-50%)"
                bg="blackAlpha.600"
                color="white"
                _hover={{ bg: "blackAlpha.800" }}
                _active={{ bg: "blackAlpha.900" }}
                borderRadius="full"
                size="lg"
                onClick={goToPrevious}
                aria-label="Previous image"
              />
              
              {/* Next Button */}
              <IconButton
                icon={<Icon as={FiChevronRight} />}
                position="absolute"
                right="10px"
                top="50%"
                transform="translateY(-50%)"
                bg="blackAlpha.600"
                color="white"
                _hover={{ bg: "blackAlpha.800" }}
                _active={{ bg: "blackAlpha.900" }}
                borderRadius="full"
                size="lg"
                onClick={goToNext}
                aria-label="Next image"
              />
              
              {/* Image Counter */}
              <Box
                position="absolute"
                bottom="10px"
                right="10px"
                bg="blackAlpha.700"
                color="white"
                px={3}
                py={1}
                borderRadius="md"
                fontSize="sm"
                fontWeight="medium"
              >
                {currentImageIndex + 1} / {allImages.length}
              </Box>
            </>
          )}
        </Box>
      )}
    </Box>
  );
};

export default TeamActivityHeroImage; 